import { useTranslation } from "react-i18next";
import { Loader2, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill, type PillTone } from "@/components/brand/Pill";
import type { ReferenceDataRun } from "@/types/reference-data";
import { formatDateTime } from "@/lib/datetime";
import { operationLabel, runStatusLabel } from "@/lib/reference-data-labels";

export const PAGE_SIZE = 50;
// Audit log shows this many runs before the "Show all" toggle.
export const AUDIT_PREVIEW = 5;

export function RunStatusPill({ status }: { status?: string }) {
  const { t } = useTranslation();
  const map: Record<string, { tone: PillTone; label: string; live?: boolean }> = {
    running: { tone: "gold", label: t("Running"), live: true },
    queued: { tone: "gold", label: t("Queued"), live: true },
    success: { tone: "ok", label: t("Complete") },
    failed: { tone: "danger", label: t("Failed") },
    partial: { tone: "warn", label: t("Partial") },
  };
  const s = status ? map[status] : undefined;
  if (!s) return null;
  return (
    <Pill tone={s.tone} live={s.live}>
      {s.label}
    </Pill>
  );
}

export function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: "ok" | "gold";
}) {
  const color = accent === "ok" ? "#27a155" : accent === "gold" ? "#b57a00" : "#08254c";
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#596b85]">{label}</div>
      <div className="font-brand font-extrabold text-[22px]" style={{ color }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

const OP_TONE: Record<string, PillTone> = {
  upload: "muted",
  ingest: "navy",
  edit: "navy",
  promote: "ok",
  rollback: "warn",
  discard: "danger",
};

export function AuditRow({ run }: { run: ReferenceDataRun }) {
  const { t } = useTranslation();
  const report = run.report;
  const summary =
    report?.outcome === "staged" || report?.outcome === "rejected"
      ? t("{{rows}} rows · {{n}} new · {{u}} updated", {
          rows: report.row_count ?? 0,
          n: report.new_count ?? 0,
          u: report.updated_count ?? 0,
        })
      : report?.outcome === "promoted" || report?.outcome === "rolled_back"
        ? t("{{a}} activated · {{d}} demoted", {
            a: report.activated_rows ?? 0,
            d: report.demoted_rows ?? 0,
          })
        : report?.filename
          ? String(report.filename)
          : "";

  return (
    <div className="flex items-start gap-3 px-3 py-3">
      <History className="w-3.5 h-3.5 text-[#b2bccb] mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Pill tone={OP_TONE[run.operation_type] ?? "muted"}>
            {operationLabel(t, run.operation_type)}
          </Pill>
          <span
            className={`mono text-[10px] uppercase tracking-[0.06em] ${
              run.status === "success"
                ? "text-[#27a155]"
                : run.status === "failed"
                  ? "text-[#d4353c]"
                  : "text-[#7b8799]"
            }`}
          >
            {runStatusLabel(t, run.status)}
          </span>
        </div>
        {summary && <div className="text-[12px] text-[#596b85] mt-1">{summary}</div>}
        <div className="mono muted text-[11px] mt-0.5">
          {run.initiated_by_name ? `${run.initiated_by_name} · ` : ""}
          {formatDateTime(run.created_at)}
        </div>
      </div>
    </div>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="py-10 text-center text-[#7b8799] text-sm">
      <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
      {t("Loading…")}
    </div>
  );
}

export function Empty({ text }: { text: string }) {
  return <div className="py-12 text-center text-[#7b8799] text-sm">{text}</div>;
}

/** Falha de uma chamada da API: o motivo, e "Tentar de novo" quando há o que refazer. */
export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="space-y-3 text-center text-sm text-[#b02a31]">
      <p>{message}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          {t("Try again")}
        </Button>
      )}
    </div>
  );
}

/** Aviso discreto de quando o refetch em segundo plano falhou mas há dado em cache.
 *  `message` troca o texto padrão quando o aviso é de um dado específico; `onRetry`
 *  acrescenta o "Tentar de novo" quando o dado falhou sem nenhum cache. */
export function StaleNotice({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <p role="status" className="mono muted text-[11px] mb-3">
      {message ?? t("Could not refresh the data; showing the last loaded version.")}
      {onRetry && (
        <>
          {" "}
          <button type="button" className="underline underline-offset-2" onClick={onRetry}>
            {t("Try again")}
          </button>
        </>
      )}
    </p>
  );
}
